import { useEffect, useState } from 'react';
import { Grid, MenuItem } from '@mui/material';
import { ExpansionPanel, InputField, CurrencySelect, MoneyField } from '@/components/common';
import { useCompanyAnnualStore } from '@/store/audit/companyAnnualStore';
import type { CompanyAnnual } from '@/services/accountService/companyAnnualService';
import { currencySymbolOf } from '@/components/common/currencyData';

type Props = {
  annualId: number;
  onChange?: (patch: Partial<CompanyAnnual>) => void;
  initialData?: {
    dividendPaid?: number;
    dividendCurrency?: string | null;
    amountPerEachShare?: number;
    totalDividendAmount?: number;
  } | null;
};

export default function EpDividend({ annualId, onChange, initialData }: Props) {
  const getDividendInfo = useCompanyAnnualStore(s => s.getDividendInfo);
  const [local, setLocal] = useState<{
    dividendPaid?: number;
    dividendCurrency: string | null;
    amountPerEachShare?: number;
    totalDividendAmount?: number;
  }>({
    dividendPaid: undefined,
    dividendCurrency: 'HKD',
    amountPerEachShare: undefined,
    totalDividendAmount: undefined,
  });

  useEffect(() => {
    if (initialData) {
      setLocal({
        dividendPaid: initialData.dividendPaid,
        dividendCurrency: initialData.dividendCurrency || 'HKD',
        amountPerEachShare: initialData.amountPerEachShare,
        totalDividendAmount: initialData.totalDividendAmount,
      });
      return;
    }

    let mounted = true;
    (async () => {
      if (!annualId) return;
      const v = await getDividendInfo(annualId);
      if (!mounted) return;
      setLocal({
        dividendPaid: v.dividendPaid,
        dividendCurrency: v.dividendCurrency || 'HKD',
        amountPerEachShare: v.amountPerEachShare,
        totalDividendAmount: v.totalDividendAmount,
      });
    })();
    return () => { mounted = false; };
  }, [annualId, getDividendInfo, initialData]);

  const emit = (patch: Partial<typeof local>) => {
    const next = { ...local, ...patch };
    setLocal(next);
    onChange?.(patch as Partial<CompanyAnnual>);
  };

  const paid = local.dividendPaid;

  return (
    <ExpansionPanel title="Dividend (派息)">
      <Grid container spacing={0.5}>
        <Grid size={12 as any}>
          <InputField
            select
            fullWidth
            variant="outlined"
            label="Dividend paid? (是否派息?)"
            value={paid ?? ''}
            onChange={(e) => emit({ dividendPaid: Number(e.target.value) as any })}
          >
            <MenuItem dense value="">Select</MenuItem>
            <MenuItem dense value={0}>No Dividend Paid (沒有派股息)</MenuItem>
            <MenuItem dense value={1}>Year End Dividend (有派年結股息)</MenuItem>
            <MenuItem dense value={2}>Interm Dividend (有派中期股息)</MenuItem>
          </InputField>
        </Grid>
        {(paid === 1 || paid === 2) && (
          <>
            <Grid size={6 as any}>
              <CurrencySelect
                variant="outlined"
                label="Dividend Currency (派股息的貨幣)"
                value={local.dividendCurrency || 'HKD'}
                onChange={(code: string) => emit({ dividendCurrency: code })}
              />
            </Grid>
            <Grid size={6 as any}>
              <MoneyField
                label="Amount per each share (每股派息)"
                value={local.amountPerEachShare ?? 0}
                showCents
                editable
                align="left"
                currencySymbol={currencySymbolOf(local.dividendCurrency || 'HKD')}
                onChangeNumber={(n) => emit({ amountPerEachShare: Number(n || 0) })}
              />
            </Grid>
            <Grid size={6 as any}>
              <MoneyField
                label="Total money amount of dividend paid (總派息金額)"
                value={local.totalDividendAmount ?? 0}
                showCents
                editable
                align="left"
                currencySymbol={currencySymbolOf(local.dividendCurrency || 'HKD')}
                onChangeNumber={(n) => emit({ totalDividendAmount: Number(n || 0) })}
              />
            </Grid>
          </>
        )}
      </Grid>
    </ExpansionPanel>
  );
}
